import { cn } from '../lib/cn.js'

/**
 * The Capture Fair mark and wordmark.
 *
 * Drawn as SVG rather than shipped as an image: it stays sharp at every size,
 * costs a few hundred bytes instead of a download, and takes its colours from
 * the theme, so a palette change reaches it too.
 *
 * `currentColor` is deliberately not used — the mark is two colours by
 * definition, and letting it inherit would produce a monochrome logo the first
 * time somebody placed it on a coloured surface.
 */

const PETROL = '#2b6777'
const GREEN = '#52ab98'

/** The CF monogram on its own. For narrow places and as a favicon. */
export function LogoMark({ className, title }: { className?: string; title?: string }) {
  return (
    <svg
      viewBox="0 0 184 176"
      className={cn('h-8 w-auto', className)}
      role={title ? 'img' : 'presentation'}
      aria-label={title}
      aria-hidden={title ? undefined : true}
    >
      {/*
        The C: an open arc, not a letter from a font — it has to line up with
        the F at every size, and a glyph would shift with whatever font stack
        the system supplies.

        Centre (72, 88), radius 58, ends at ±50° so the opening faces right.
        The stroke is 19 wide, so the arc reaches x = 118 at its widest; the F
        starts at 124. That gap is the whole point: at 28 pixels — the size the
        header actually uses — a mark whose two halves touch reads as one blob.
      */}
      <path
        d="M 109 44 A 58 58 0 1 0 109 132"
        fill="none"
        stroke={PETROL}
        strokeWidth="19"
        strokeLinecap="butt"
      />
      {/* The F. Its arms are cut at an angle, which is what keeps the mark from
          looking like a stencil. */}
      <path d="M 124 22 h 21 v 132 h -21 z" fill={GREEN} />
      <path d="M 124 22 h 60 l -15 21 h -45 z" fill={GREEN} />
      <path d="M 124 74 h 48 l -15 21 h -33 z" fill={GREEN} />
    </svg>
  )
}

/** Mark plus wordmark. The full lockup, for the sign-in page and the header. */
export function Logo({
  className,
  withDomain = false,
}: {
  className?: string
  withDomain?: boolean
}) {
  return (
    <span className={cn('flex items-center gap-3', className)}>
      <LogoMark className="h-9 w-auto shrink-0" />
      <span className="flex flex-col leading-none">
        <span className="text-[1.05em] font-semibold tracking-tight">
          <span style={{ color: PETROL }}>Capture</span> <span style={{ color: GREEN }}>Fair</span>
        </span>
        {withDomain && (
          <span className="text-text-muted mt-1 text-[0.62em] tracking-[0.22em]">
            capturefair.com
          </span>
        )}
      </span>
    </span>
  )
}
